import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AccessibilityContext = createContext(null);

const STORAGE_KEY = 'hoi-buur-a11y';

const defaults = {
  fontSize: 'normal',    // 'small' | 'normal' | 'large' | 'xlarge'
  highContrast: false,
  reduceMotion: false,
};

export const AccessibilityProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...defaults, ...JSON.parse(stored) } : defaults;
    } catch {
      return defaults;
    }
  });

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;

    // Font size
    const fontScales = { small: '14px', normal: '16px', large: '18px', xlarge: '20px' };
    root.style.fontSize = fontScales[settings.fontSize] || '16px';

    // High contrast
    root.classList.toggle('high-contrast', settings.highContrast);

    // Reduce motion
    root.classList.toggle('reduce-motion', settings.reduceMotion);

    // Persist
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  return (
    <AccessibilityContext.Provider value={{ settings, updateSetting }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return context;
};
